import { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { PayoutResponse } from '../api'
import { forgetToken } from '../_shared/token'
import {
  Badge,
  Button,
  Card,
  ErrorPanel,
  Footer,
  Header,
  Page,
} from '../_shared/ui'
import { formatMoney } from '../_shared/format'

export default function Confirm() {
  const { surveyId = '' } = useParams()
  const location = useLocation()
  const data = location.state as PayoutResponse | undefined

  useEffect(() => {
    // The token is single-use; once we land on the confirm screen we
    // remove it so a refresh of the panellist page doesn't re-prompt.
    if (data?.ok) forgetToken(surveyId)
  }, [data, surveyId])

  if (!data || !('ok' in data) || !data.ok) {
    return (
      <Page>
        <Header />
        <ErrorPanel
          title="No submission found"
          body={
            <>
              We could not find a recent submission. If you've already completed payment, check
              your email for the verifiable receipt.
            </>
          }
        />
        <Footer surveyId={surveyId} />
      </Page>
    )
  }

  return (
    <Page>
      <Header
        surveyName={data.dryRun ? 'Submitted (test mode)' : 'Submitted'}
        sponsorName={`${data.tier} · ${formatMoney(data.amountCents / 100, data.currency)}`}
        badge={
          data.dryRun ? (
            <Badge variant="warning">Test mode</Badge>
          ) : (
            <Badge variant="success">Recorded</Badge>
          )
        }
      />

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              background: data.dryRun ? 'var(--cp-warning-bg)' : 'var(--cp-success-bg)',
              borderRadius: 'var(--cp-radius-md)',
              border: `1px solid ${data.dryRun ? 'var(--cp-warning)' : 'var(--cp-success)'}`,
            }}
          >
            <CheckCircle dry={data.dryRun} />
            <div>
              <div style={{ fontWeight: 600 }}>
                {data.dryRun ? 'Test submission recorded' : 'Payment submitted'}
              </div>
              <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-secondary)' }}>
                {data.expectedSettlement}
              </div>
            </div>
          </div>

          {data.dryRun && (
            <div
              style={{
                fontSize: 'var(--cp-text-sm)',
                color: 'var(--cp-fg-secondary)',
                background: 'var(--cp-bg-code)',
                padding: '10px 12px',
                borderRadius: 'var(--cp-radius-md)',
              }}
            >
              The sponsor has this survey in <strong>test mode</strong>. Your details have been
              recorded for testing but no money has been sent. You can re-submit on a live
              survey using the same flow.
            </div>
          )}

          <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-secondary)' }}>
            Your verifiable receipt is available at the link below. Bookmark it — you can return
            to it any time to verify the on-chain anchor once settlement completes.
          </div>

          <Button
            onClick={() => {
              window.location.href = data.publicReceiptUrl
            }}
            fullWidth
          >
            Open your receipt
          </Button>

          <div style={{ fontSize: 'var(--cp-text-xs)', color: 'var(--cp-fg-muted)', textAlign: 'center' }}>
            <code className="cp-mono">{data.publicReceiptUrl}</code>
          </div>
        </div>
      </Card>

      <Footer surveyId={surveyId} />
    </Page>
  )
}

function CheckCircle({ dry }: { dry: boolean }) {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke={dry ? 'var(--cp-warning-dark)' : 'var(--cp-success-dark)'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}
